// @purpose Deep-link helpers for AI Applications ↔ Incidents / Concerns / Detection Rules navigation — builds the `?item=`-based incident and concern permalinks, sensitive-data concern links, and the cross-tab Detections/Signals drill helpers (which pass `location.state` rather than query params). Use these rather than hand-building any AI-security URL, so one scheme is maintained.
/**
 * Deep-link helpers for AI Applications ↔ Incidents / Concerns navigation.
 *
 * The Incidents page routes its detail drawer through the shared dashboard
 * base, which owns the `?item=<id>` permalink (redirected on load to the
 * hidden `ids` filter so the grid scopes to that one incident). Callers on
 * the AI Applications catalog, application detail, drawers, and briefings use
 * these helpers so a single URL scheme is maintained.
 *
 * The cross-tab drill helpers (Detections + Signals) use `location.state`
 * rather than query params — matches the pattern SecurityEvents already
 * consumes for `eventIds` and keeps URLs clean when the trigger is a
 * transient in-app action (drawer button, row click).
 */

import type { NavigateFunction } from 'react-router'

const INCIDENTS_PATH = '/ai-applications/incidents'
const CONCERNS_PATH = '/ai-applications/security-concerns'
const CONCERNS_DETECTIONS_PATH = `${CONCERNS_PATH}/detections`
const CONCERNS_SIGNALS_PATH = `${CONCERNS_PATH}/signals`

// ---------------------------------------------------------------------------
// Cross-page links to the standalone AI Detection Rules dashboard
// ---------------------------------------------------------------------------

const DETECTION_RULES_PATH = '/ai-applications/detection-rules'

// Maps a singular catalog kind to the Detection Rules page's plural tab id.
// Mirrors that page's KIND_TO_TAB so a link lands on the matching tier tab.
const KIND_TO_GRID: Record<'sig' | 'det' | 'type', string> = {
  sig: 'sigs',
  det: 'dets',
  type: 'types',
}

/** Path to the AI Detection Rules dashboard, on the tab for `kind`, filtered to
 *  the single catalog row whose code matches `code`. Matches the dashboard's own
 *  in-tab cross-link behavior (`?grid=<tab>&code=<code>`): the target tier is
 *  narrowed to the one row; the drawer is not auto-opened (the user clicks the
 *  row). Used by the Concern/Incident Composition-detail and Metadata chips. */
export const detectionRulesPath = (
  kind: 'sig' | 'det' | 'type',
  code: string,
): string =>
  `${DETECTION_RULES_PATH}?grid=${KIND_TO_GRID[kind]}&code=${encodeURIComponent(code)}`

export const incidentDeepLink = (incidentId: string): string =>
  `${INCIDENTS_PATH}?item=${encodeURIComponent(incidentId)}`

export const concernDeepLink = (concernId: string): string =>
  `${CONCERNS_PATH}?concern=${encodeURIComponent(concernId)}`

export const CONCERN_QUERY_PARAM = 'concern'

// PENG-13307: sec_type_code for the two sensitive-data concern types the
// Shadow AI page's Sensitive Data column surfaces. The ticket's shorthand
// (SEC-TYPE-28/29) doesn't match a real seeded code — TYPE-11/TYPE-12 are the
// actual PCI-DSS-EXPOSURE/PII-EXPOSURE definitions.
export type SensitiveDataConcernType = 'TYPE-11' | 'TYPE-12'

/** Strips an optional ":port" suffix and lower-cases the rest — mirrors the
 *  backend's dst_host normalization (utils.NormalizeHostSQL / utils.
 *  NormalizeHost in cnapp-api/pkg/utils). destinationDomain is not normalized
 *  at write time, so both the deep-link builder and the Concerns page filter
 *  reading it back must apply this identically, or a port-bearing/differently
 *  -cased destination silently never matches Concern.Destinations. */
export const normalizeDestinationHost = (host: string): string => {
  const colonIndex = host.indexOf(':')
  return (colonIndex >= 0 ? host.slice(0, colonIndex) : host).toLowerCase()
}

/** Deep-link to the Concerns page, pre-filtered to a destination + sensitive-
 *  data concern type — the Shadow AI page's Sensitive Data chips use this. */
export const sensitiveDataDeepLink = (
  destination: string,
  type: SensitiveDataConcernType,
): string =>
  `${CONCERNS_PATH}?destination=${encodeURIComponent(normalizeDestinationHost(destination))}&type=${type}`

// ---------------------------------------------------------------------------
// Cross-tab drill-outs on the Concerns page
// ---------------------------------------------------------------------------

/** Shape of the transient state the Concerns tabs read on mount. Each field
 *  is optional; consumers hook into just the ones they care about. */
export type ConcernsDrillState = {
  /** Detections tab: open the DET drawer for this SEC-DET id on mount. */
  openDetId?: string
  /** Detections tab: filter rows to DETs composing this SEC-TYPE id. */
  filterTypeId?: string
  /** Signals tab: filter events whose backing rule id matches. */
  sigRuleIds?: string[]
  /** Signals tab: filter to an exact set of security-event ids (the reliable
   *  drill-in used by the Composition-detail "View all fires" button). */
  eventIds?: string[]
}

/** Switch to the Detections tab and open a specific SEC-DET's drawer. */
export const openDetectionDrawer = (
  navigate: NavigateFunction,
  detId: string,
): void => {
  const state: ConcernsDrillState = { openDetId: detId }
  navigate(CONCERNS_DETECTIONS_PATH, { state })
}

/** Switch to the Detections tab, scoped to the SEC-DETs composing a SEC-TYPE. */
export const openDetectionsForType = (
  navigate: NavigateFunction,
  typeId: string,
): void => {
  const state: ConcernsDrillState = { filterTypeId: typeId }
  navigate(CONCERNS_DETECTIONS_PATH, { state })
}

/** Switch to the Signals tab, filtered to a specific set of SEC-SIG rule ids. */
export const openSignalsForSigs = (
  navigate: NavigateFunction,
  sigRuleIds: string[],
): void => {
  const state: ConcernsDrillState = { sigRuleIds }
  navigate(CONCERNS_SIGNALS_PATH, { state })
}

/** Switch to the Signals tab, filtered to the exact security-event ids behind a
 *  set of SIG fires. SecurityEvents consumes `eventIds` as an exact `ids`
 *  filter — the reliable counterpart to `openSignalsForSigs`' rule-code text
 *  search, used by the Composition-detail "View all fires →" button. */
export const openSignalsForEvents = (
  navigate: NavigateFunction,
  eventIds: string[],
): void => {
  const state: ConcernsDrillState = { eventIds }
  navigate(CONCERNS_SIGNALS_PATH, { state })
}

/** Path to an AI Application's catalog detail page — shared by
 *  openApplicationDetail and declarative `<Link>` usages so both stay in sync on encoding. */
export const applicationDetailPath = (appId: string): string =>
  `/ai-applications/catalog/${encodeURIComponent(appId)}`

/** Path to the Component Catalog narrowed to one component (PENG-14234):
 *  `?ids=` filters to exactly its catalog items. Null when it has none -- the
 *  catalog lists no row for the component's service (it was never classified
 *  as an AI workload), or an older payload omits them -- so callers render the
 *  name as plain text. A name-search fallback would land on unrelated rows:
 *  other components whose host, attributed application names or longer names
 *  contain it, or a same-named service in another cluster. The catalog has no
 *  per-component route. */
export const componentCatalogPath = (component: {
  readonly catalogItemIds: ReadonlyArray<string>
}): string | null =>
  component.catalogItemIds.length > 0
    ? `/ai-applications/component-catalog?ids=${encodeURIComponent(component.catalogItemIds.join(','))}`
    : null

/** Navigate the SPA router to an AI Application's catalog detail page,
 *  replacing the legacy `window.location.href` full-page reload. */
export const openApplicationDetail = (
  navigate: NavigateFunction,
  appId: string,
): void => {
  navigate(applicationDetailPath(appId))
}

// ---------------------------------------------------------------------------
// Deep links to the AI Identities grid
// ---------------------------------------------------------------------------

// /ai-applications/identities is a <Navigate replace> that DROPS the query string,
// so links target the doubled-segment tab route directly (App.tsx). Keep this the
// ONLY place the identities route/param shape is expressed.
export const IDENTITIES_TAB_PATH = '/ai-applications/identities/identities'

/** Deep link to the AI Identities grid pre-filtered to one identity.
 *
 * Filtering keys off `identityId` — the wire id ("{provider}:{base64url(arn)}")
 * the identities list uses as its row id — so the link survives a rename;
 * `identityName` rides along only to label the grid's removable filter chip
 * and is omitted when there is none. */
export const aiIdentityDeepLink = (
  identityId: string,
  identityName?: string,
): string => {
  const params = new URLSearchParams({ identityId })
  if (identityName) params.set('identityName', identityName)
  return `${IDENTITIES_TAB_PATH}?${params.toString()}`
}

const DEFAULT_IDENTITY_RISK_BAND: readonly string[] = ['critical', 'high']

/** Deep link to the AI Identities grid pre-filtered to a risk-level band.
 *
 * riskLevel is comma-joined RAW: IdentitiesTab's riskLevel filter is
 * multiple:true and BasicSearch comma-splits it. The comma is a legal
 * RFC3986 sub-delimiter — do NOT run the whole value through
 * URLSearchParams (that would %2C-encode it and change the copyable URL).
 *
 * Falls back to the default band when the descriptor supplies nothing usable
 * (undefined, [], or only blank entries), which would otherwise join to a
 * filter-clearing `?riskLevel=`. */
export const aiIdentityRiskListDeepLink = (
  riskLevels?: readonly string[],
): string => {
  const levels = riskLevels?.filter(level => level.length > 0) ?? []
  const band = levels.length > 0 ? levels : DEFAULT_IDENTITY_RISK_BAND
  // Encode each band VALUE but keep the comma raw: the comma is the structural
  // sub-delimiter IdentitiesTab splits on; the values are the components. (A
  // no-op on today's enum bands, but correct for any value.)
  return `${IDENTITIES_TAB_PATH}?riskLevel=${band.map(encodeURIComponent).join(',')}`
}

// ---------------------------------------------------------------------------
// Shadow AI → AI Applications catalog
// ---------------------------------------------------------------------------

const AI_APPLICATIONS_CATALOG_PATH = '/ai-applications/catalog'

/**
 * Link to the AI Applications catalog pre-filtered to a single owning
 * application, for the Shadow AI drawer's "Owning AI application" field.
 *
 * Targets `/ai-applications/catalog` — NOT the bare `/ai-applications` the
 * plan's Data Definitions block names. `/ai-applications` is a
 * `<Navigate to="/ai-applications/catalog" replace />` redirect (App.tsx:682),
 * and Navigate with a bare path carries no search string, so
 * `/ai-applications?app_id=X` would silently arrive at the catalog with the
 * filter dropped — a link that looks right, navigates fine, and quietly does
 * nothing. `/ai-applications/catalog` is also what the existing app_id emitter
 * on the Security Concerns page uses.
 *
 * The receiving side (`readAppIdsFromUrl`, AIApplicationsPage.tsx) parses
 * `app_id` as a comma-joined list; a single id is a valid one-element case.
 */
export const owningAppDeepLink = (appId: string): string =>
  `${AI_APPLICATIONS_CATALOG_PATH}?app_id=${encodeURIComponent(appId)}`

/** Deep link to the Shadow AI page pre-filtered to one destination domain. */
export const shadowAiDeepLink = (destinationDomain: string): string =>
  `/ai-applications/shadow-ai?destinationDomain=${encodeURIComponent(destinationDomain)}`
